// features/hives/ui/hivesNew/HivesNewHive.tsx
import { FC } from "react";
import { Drawer } from "shared/ui/drawer";
import { useNewHive } from "features/hives/lib/useNewHive";
import {
    NewHiveBody,
    NewHiveBorder as Divider,
    NewHiveFooter,
    NewHiveHead,
    NewHiveRow,
    NewHiveTitle,
    NewHiveWrapper,
    Suggest,
    SuggestItem,
    SuggestWrap,
} from "features/hives/ui/hivesNew/styles";
import Button from "@atomaro/ui-kit/components/Button/Button/Button";
import { CloseIcon } from "shared/ui/icons";
import { toRem } from "shared/helpers";
import { Spacer } from "shared/ui/spacer";
import { Controller } from "react-hook-form";
import { Input } from "@atomaro/ui-kit";
import ColorPicker from "features/hives/ui/hivesNew/ColorPicker";
import { markColors } from "features/hives/ui/hivesTable/HiveMarkCell";
import Select from "@atomaro/ui-kit/components/Select/Select";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    isMobile?: boolean;
};

export const HivesNewHive: FC<Props> = ({ isOpen, onClose, isMobile }) => {
    const {
        control,
        errors,
        isSubmitting,
        onNumberChange,
        onYearChange,
        onMarkNumberChange,
        onMarkColorChange,
        onBreedChange,
        handleSubmit,
        onSubmit,
        doReset,

        markOptionsShown,
        breedOptionsShown,
        filteredMarkOptions,
        filteredBreedOptions,
        onPickMarkOption,
        onPickBreedOption,
        onMarkFocus,
        onBreedFocus,
        onMarkBlur,
        onBreedBlur,
    } = useNewHive({});

    const closeAndReset = () => {
        doReset();
        onClose();
    };

    const submitAndClose = async (form: any) => {
        const res = await onSubmit(form);
        if (res?.status === 200 || res?.status === 201) closeAndReset();
    };

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => ({
        key: String(y),
        value: String(y),
    }));

    return (
        <Drawer isOpen={isOpen} position={isMobile ? "bottom" : "right"} onClose={closeAndReset}>
            <NewHiveWrapper onSubmit={handleSubmit(submitAndClose)} className={isSubmitting ? "_skeleton" : ""}>
                <NewHiveHead $isMobile={isMobile}>
                    <Button
                        iconPrefix={<CloseIcon />}
                        variant={isMobile ? "ghost" : "secondary"}
                        colorScheme="neutral"
                        style={{ alignSelf: "flex-end", width: isMobile ? toRem(24) : toRem(36) }}
                        size={isMobile ? "s" : "m"}
                        onClick={closeAndReset}
                    />
                    <NewHiveTitle>Новый улей</NewHiveTitle>
                    {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
                </NewHiveHead>

                <NewHiveBody>
                    <NewHiveRow $alignItems="center">
                        <span>Номер улья</span>
                        <Controller
                            name="number"
                            control={control}
                            rules={{ required: { value: true, message: "Введите номер улья" } }}
                            render={({ field }) => (
                                <Input
                                    name={field.name}
                                    value={String(field.value ?? "")}
                                    size="m"
                                    placeholder="Номер улья"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^\d]/g, "");
                                        field.onChange(val);
                                        onNumberChange(val);
                                    }}
                                    error={errors.number?.message as string}
                                />
                            )}
                        />
                    </NewHiveRow>

                    <Divider />

                    <NewHiveRow $alignItems="center">
                        <span>Год матки</span>
                        <Controller
                            name="uterusYear"
                            control={control}
                            rules={{ required: { value: true, message: "Выберите год матки улья" } }}
                            render={({ field }) => (
                                <Select
                                    size="m"
                                    placeholder="Выберите год"
                                    items={years}
                                    placement="bottom"
                                    defaultValue={String(field.value ?? "")}
                                    onChange={(v) => {
                                        const num = v ? Number(v) : null;
                                        field.onChange(num);
                                        onYearChange(num);
                                    }}
                                    error={errors.uterusYear?.message}
                                    dropdownMenuStyle={{ maxHeight: 300, overflowY: "auto" }}
                                />
                            )}
                        />
                    </NewHiveRow>

                    <NewHiveRow $alignItems="center">
                        <span>Номер метки</span>
                        <SuggestWrap>
                            <Controller
                                name="uterusMark"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <Input
                                            name={field.name}
                                            value={String(field.value ?? "")}
                                            size="m"
                                            placeholder="Номер метки"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            onFocus={onMarkFocus}
                                            onBlur={(e) => onMarkBlur(e, field)}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[^\d]/g, "");
                                                field.onChange(val);
                                                onMarkNumberChange(val);
                                            }}
                                        />
                                        {markOptionsShown && filteredMarkOptions.length > 0 && (
                                            <Suggest>
                                                {filteredMarkOptions
                                                    .map((opt) => (
                                                    <SuggestItem
                                                        key={opt}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            onPickMarkOption(opt, field);
                                                        }}
                                                    >
                                                        {opt}
                                                    </SuggestItem>
                                                ))}
                                            </Suggest>
                                        )}
                                    </>
                                )}
                            />
                        </SuggestWrap>
                    </NewHiveRow>

                    <NewHiveRow $alignItems="center">
                        <span>Цвет метки</span>
                        <Controller
                            name="uterusMarkColor"
                            control={control}
                            render={({ field }) => {
                                const id = Number(field.value ?? 0);
                                const initialMainColor =
                                    markColors.find((m) => m.id === id)?.mainColor || markColors[0]?.mainColor;

                                return (
                                    <ColorPicker
                                        key={`cp-${id}`}
                                        items={markColors.map((c) => c.mainColor)}
                                        initial={initialMainColor}
                                        onChange={(cssVar) => {
                                            const nextId = markColors.find((m) => m.mainColor === cssVar)?.id ?? 0;
                                            field.onChange(nextId);
                                            onMarkColorChange(String(nextId));
                                        }}
                                    />
                                );
                            }}
                        />
                    </NewHiveRow>

                    <NewHiveRow $alignItems="center">
                        <span>Порода</span>
                        <SuggestWrap>
                            <Controller
                                name="breed"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <Input
                                            name={field.name}
                                            value={String(field.value ?? "")}
                                            size="m"
                                            placeholder="Карника"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            onFocus={onBreedFocus}
                                            onBlur={(e) => onBreedBlur(e, field)}
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                                onBreedChange(e.target.value);
                                            }}
                                        />
                                        {breedOptionsShown && filteredBreedOptions.length > 0 && (
                                            <Suggest>
                                                {filteredBreedOptions.map((opt) => (
                                                    <SuggestItem
                                                        key={opt}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            onPickBreedOption(opt, field);
                                                        }}
                                                    >
                                                        {opt}
                                                    </SuggestItem>
                                                ))}
                                            </Suggest>
                                        )}
                                    </>
                                )}
                            />
                        </SuggestWrap>
                    </NewHiveRow>
                </NewHiveBody>

                <NewHiveFooter>
                    <Button type="submit" label="Добавить" disabled={isSubmitting} />
                    <Button label="Отменить" variant="ghost" colorScheme="neutral" onClick={closeAndReset} />
                </NewHiveFooter>
            </NewHiveWrapper>
        </Drawer>
    );
};
